/**
 * DET — Domicílio Eletrônico Trabalhista — Fase 12
 * Caixa postal do MTE: polling + visualização de mensagens
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Mail, Building2, RefreshCw, Trash2, CheckCircle2, AlertTriangle, Archive, Inbox, Loader2, ArrowUpDown } from 'lucide-react';
import { useConfirm } from '../../hooks/useConfirm';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const NIVEL_BADGE = {
  informativo: 'bg-slate-100 text-slate-700',
  atencao: 'bg-amber-100 text-amber-700',
  urgente: 'bg-red-100 text-red-700',
};

const KpiPanel = ({ companyId, refreshKey }) => {
  const [s, setS] = useState({ total_mensagens: 0, nao_lidas: 0, urgentes_pendentes: 0, modo: '' });
  useEffect(() => {
    axios.get(`${API}/api/det/status`, { ...auth(), params: companyId ? { company_id: companyId } : {} })
      .then(r => setS(r.data)).catch(() => {});
  }, [companyId, refreshKey]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="det-kpis">
      <div className="border rounded-lg p-3 bg-slate-50"><div className="text-xs">Total mensagens</div><div className="text-2xl font-bold">{s.total_mensagens}</div></div>
      <div className="border rounded-lg p-3 bg-blue-50"><div className="text-xs">Não lidas</div><div className="text-2xl font-bold text-blue-700">{s.nao_lidas}</div></div>
      <div className="border rounded-lg p-3 bg-red-50"><div className="text-xs">Urgentes</div><div className="text-2xl font-bold text-red-700">{s.urgentes_pendentes}</div></div>
      <div className="border rounded-lg p-3 bg-amber-50"><div className="text-xs">Modo</div><div className="text-lg font-bold">{s.modo?.toUpperCase()}</div></div>
    </div>
  );
};

export default function DetPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [filtro, setFiltro] = useState('todas');
  const [sortAsc, setSortAsc] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [open, setOpen] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    axios.get(`${API}/api/companies`, auth()).then(r => {
      setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { company_id: companyId, limit: 200, sort_order: sortAsc ? 'asc' : 'desc' };
      if (filtro === 'nao_lidas') params.lida = false;
      else if (filtro === 'urgentes') { params.nivel = 'urgente'; params.lida = false; }
      const r = await axios.get(`${API}/api/det/mensagens`, { ...auth(), params });
      setMsgs(r.data || []);
    } finally { setLoading(false); }
  }, [companyId, filtro, sortAsc]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const poll = async () => {
    if (!companyId) return;
    setPolling(true);
    try {
      const r = await axios.post(`${API}/api/det/poll/${companyId}`, {}, auth());
      toast.success(`Polling concluído — ${r.data.novas_mensagens} nova(s)`);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro');
    } finally { setPolling(false); }
  };

  const abrir = async (m) => {
    const r = await axios.get(`${API}/api/det/mensagens/${m.id}`, auth());
    setOpen(r.data);
  };

  const marcarLida = async (m) => {
    await axios.post(`${API}/api/det/mensagens/${m.id}/marcar-lida`, {}, auth());
    toast.success('Marcada como lida');
    setOpen(null); setRefreshKey(k => k + 1);
  };

  const remover = async (m) => {
    const ok = await confirm({ title: 'Arquivar mensagem?', description: m.assunto });
    if (!ok) return;
    await axios.delete(`${API}/api/det/mensagens/${m.id}`, auth());
    setOpen(null); setRefreshKey(k => k + 1);
  };

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="det-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="h-7 w-7 text-indigo-600" />DET — Caixa Postal MTE <Badge variant="outline" className="ml-2">Fase 12</Badge></h1>
          <p className="text-sm text-slate-500">Notificações trabalhistas do Ministério do Trabalho</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={companyId || ''} onValueChange={setCompanyId}>
            <SelectTrigger className="w-64" data-testid="det-company-select"><Building2 className="h-4 w-4 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.razao_social}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={poll} disabled={polling || !companyId} data-testid="det-poll-btn">
            {polling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Buscar Novas
          </Button>
          <Button variant="outline" onClick={() => navigate('/dp-folha/det/arquivadas')} data-testid="det-arquivadas-btn">
            <Archive className="h-4 w-4 mr-2" />
            Arquivadas
          </Button>
        </div>
      </div>
      <KpiPanel companyId={companyId} refreshKey={refreshKey} />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Inbox className="h-5 w-5 text-indigo-600" />Caixa de Entrada</CardTitle>
            <CardDescription>Mensagens recebidas do MTE</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filtro} onValueChange={setFiltro}>
              <SelectTrigger className="w-44" data-testid="det-filtro"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="nao_lidas">Não lidas</SelectItem>
                <SelectItem value="urgentes">Urgentes pendentes</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={() => setSortAsc(p => !p)} data-testid="det-sort-btn" title={sortAsc ? 'Mais antigas primeiro' : 'Mais recentes primeiro'}>
              <ArrowUpDown className={`h-4 w-4 ${sortAsc ? 'text-indigo-600' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-2">
              {msgs.length === 0 && <div className="text-center text-slate-400 py-8">Nenhuma mensagem</div>}
              {msgs.map(m => (
                <button key={m.id}
                  onClick={() => abrir(m)}
                  data-testid={`det-msg-${m.id}`}
                  className={`w-full text-left border rounded-lg p-3 hover:bg-slate-50 ${m.lida ? 'opacity-60' : 'bg-white'}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {!m.lida && <span className="h-2 w-2 rounded-full bg-blue-500" />}
                      {m.assunto}
                    </div>
                    <Badge className={NIVEL_BADGE[m.nivel] || ''}>{m.nivel}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{m.remetente} · Prazo: {m.prazo_resposta || '—'} · {(m.criado_em || '').slice(0, 16).replace('T', ' ')}</div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!open} onOpenChange={v => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="det-msg-dialog">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {open.nivel === 'urgente' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                  {open.assunto}
                </DialogTitle>
                <DialogDescription>{open.remetente} · {(open.data_envio || '').slice(0, 16).replace('T', ' ')} · Prazo: {open.prazo_resposta || '—'}</DialogDescription>
              </DialogHeader>
              <div className="text-sm whitespace-pre-wrap py-2">{open.corpo}</div>
              <div className="flex gap-2 justify-end mt-3">
                {!open.lida && <Button onClick={() => marcarLida(open)} variant="default" data-testid="det-marcar-lida-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Marcar como lida</Button>}
                <Button onClick={() => remover(open)} variant="outline"><Trash2 className="h-4 w-4 mr-1 text-red-600" />Arquivar</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );
}
